import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const VARIABLE_HIGHLIGHT_PLUGIN_KEY = new PluginKey(
  'variableHighlightDecoration'
);

const VariableHighlightDecoration = Extension.create({
  name: 'variableHighlightDecoration',

  addOptions() {
    return {
      supportedVariables: [],
      decorationClass: 'variable-decoration',
    };
  },

  addProseMirrorPlugins() {
    const decorationClass = this.options.decorationClass;
    let currentView = null;

    return [
      new Plugin({
        key: VARIABLE_HIGHLIGHT_PLUGIN_KEY,
        state: {
          init(_, { options }) {
            return {
              supportedVariables: options?.supportedVariables || [],
            };
          },
          apply(tr, pluginState) {
            const newSupportedVariables = tr.getMeta(
              VARIABLE_HIGHLIGHT_PLUGIN_KEY
            );
            if (newSupportedVariables) {
              return {
                ...pluginState,
                supportedVariables: newSupportedVariables,
              };
            }
            return pluginState;
          },
        },
        props: {
          decorations: (state, view) => {
            currentView = view;

            const pluginState = VARIABLE_HIGHLIGHT_PLUGIN_KEY.getState(state);
            if (!pluginState) {
              return null;
            }

            const decorations = [];
            const regex = /{{(.*?)}}/g;
            const { supportedVariables } = pluginState || {};

            state.doc.descendants((node, pos) => {
              if (node.isText) {
                let match;
                while ((match = regex.exec(node.text)) !== null) {
                  const fullMatch = match[0];
                  const variable = match[1].trim();
                  const isSupported = supportedVariables.includes(variable);

                  const start = pos + match.index;
                  const end = start + fullMatch.length;

                  if (isSupported) {
                    decorations.push(
                      Decoration.inline(start, end, {
                        class: `${decorationClass} supported`,
                      })
                    );
                  } else {
                    decorations.push(
                      Decoration.inline(start, end, {
                        class: `${decorationClass} unsupported`,
                      })
                    );

                    const xButton = document.createElement('span');
                    xButton.textContent = '✕';
                    xButton.className = 'remove-variable-x';

                    xButton.onclick = (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (currentView) {
                        const { state, dispatch } = currentView;
                        const transaction = state.tr.delete(start, end);
                        dispatch(transaction);
                      }
                    };

                    decorations.push(
                      Decoration.widget(end, xButton, { side: -1 })
                    );
                  }
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
        // Capture the editor view using the 'view' lifecycle method
        view(editorView) {
          currentView = editorView;
          return {
            update(view) {
              currentView = view;
            },
            destroy() {
              currentView = null;
            },
          };
        },
      }),
    ];
  },

  addCommands() {
    return {
      updateVariablesList:
        (variables) =>
        ({ state, dispatch }) => {
          const pluginState = VARIABLE_HIGHLIGHT_PLUGIN_KEY.getState(state);
          if (pluginState) {
            // Dispatch a transaction to update the plugin's state
            dispatch(
              state.tr.setMeta(VARIABLE_HIGHLIGHT_PLUGIN_KEY, variables)
            );
            return true;
          }
          return false;
        },
    };
  },
});

export default VariableHighlightDecoration;
